/* =====================================================================================
   Canon: the policies and procedures the company is bound by.

   The name is deliberate. Forum's modules are named for the parts of a Roman forum, and a canon
   is the authoritative body of rules a community agrees to be bound by: not a folder, but the set
   of things that are settled. It covers procedures as well as policies, which "Policies" does not.

   TWO THINGS THIS SCREEN EXISTS TO MAKE UNAVOIDABLE.

   1. DRAFT IS NOT APPROVED, and the difference is the whole point of holding a policy. A register
      that renders everything identically lets a draft anti-bribery policy read as protection it
      does not yet provide. Status is the loudest thing on each card.

   2. OPEN DECISIONS ARE ON THE REGISTER, not buried on page four of the document. Seven of these
      drafts cannot be approved until somebody answers a question, and a question nobody can see
      is a question nobody answers.

   The device quality system is NOT here. That is Quality, the ISO 13485 world. A notified body
   audits one and an investor reads the other.
   ===================================================================================== */

import { useCallback, useEffect, useState } from 'react';
import { BACKEND } from '../lib/db';
import { TENANT } from '@tenant';
import './canon.css';

interface Doc {
  id: string; doc_id: string; title: string; category: string; tier: number;
  summary: string | null; status: string; version: string; owner_name: string | null;
  approved_on: string | null; approved_by: string | null; approval_note: string | null;
  review_due: string | null; open_decisions: string | null;
  file_path: string | null; file_size: number | null;
}

interface Decision {
  id: string; document_id: string; question: string; detail: string | null;
  status: string; resolution: string | null; resolved_at: string | null;
}

const CATEGORY: Record<string, string> = {
  governance: 'Governance', data_protection: 'Data protection', security: 'Security',
  commercial: 'Commercial', people: 'People', finance: 'Finance', quality: 'Quality',
};
const STATUS: Record<string, string> = {
  draft: 'Draft', in_review: 'In review', approved: 'Approved',
  superseded: 'Superseded', withdrawn: 'Withdrawn',
};

const size = (b: number | null) =>
  b == null ? '' : b < 1024 * 1024 ? `${Math.round(b / 1024)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

export function CanonView() {
  const [rows, setRows] = useState<Doc[] | null>(null);
  const [decs, setDecs] = useState<Decision[]>([]);
  /* Whether THIS user may approve. Asked of the database rather than inferred from a role list in
     the client, because the database is what will actually refuse, and a button that appears and
     then fails is worse than one that never appeared. */
  const [mayApprove, setMayApprove] = useState(false);
  const [answering, setAnswering] = useState<string | null>(null);
  const [answer, setAnswer] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (BACKEND !== 'supabase') { setRows([]); return; }
    try {
      const { supabase } = await import('../lib/supabase');
      const { data, error } = await supabase().schema('app').from('canon_documents')
        .select('id, doc_id, title, category, tier, summary, status, version, owner_name,'
              + ' approved_on, approved_by, approval_note, review_due, open_decisions,'
              + ' file_path, file_size')
        .eq('is_current', true).order('category').order('doc_id');
      if (error) throw new Error(error.message);
      setRows((data ?? []) as unknown as Doc[]);

      const [{ data: dd }, { data: may }] = await Promise.all([
        supabase().schema('app').from('canon_decisions')
          .select('id, document_id, question, detail, status, resolution, resolved_at')
          .order('status').order('question'),
        supabase().schema('app').rpc('canon_may_approve'),
      ]);
      setDecs((dd ?? []) as unknown as Decision[]);
      setMayApprove(may === true);
    } catch (e) { setErr(String((e as Error).message)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function download(d: Doc) {
    if (!d.file_path) return;
    setBusy(d.id);
    try {
      const { supabase } = await import('../lib/supabase');
      const { data, error } = await supabase().storage.from('canon')
        .createSignedUrl(d.file_path, 120, { download: true });
      if (error) throw new Error(error.message);
      window.location.href = data.signedUrl;
    } catch (e) { setMsg(String((e as Error).message)); }
    finally { setBusy(null); }
  }

  async function upload(d: Doc, file: File) {
    setBusy(d.id); setMsg(null);
    try {
      const { supabase } = await import('../lib/supabase');
      const { data: org } = await supabase().schema('app').rpc('current_org_id');
      /* Foldered by org because the storage policies key on the first path segment. Getting this
         wrong uploads successfully and then cannot be read back by anyone, including the person
         who uploaded it. */
      const path = `${org}/${d.doc_id}-${d.version}-${file.name.replace(/[^\w.\-]+/g, '_')}`;
      const { error: upErr } = await supabase().storage.from('canon')
        .upload(path, file, { upsert: true, contentType: file.type || undefined });
      if (upErr) throw new Error(upErr.message);
      const { error: rowErr } = await supabase().schema('app').from('canon_documents')
        .update({ file_path: path, mime: file.type, file_size: file.size }).eq('id', d.id);
      if (rowErr) throw new Error(`Uploaded, but not recorded: ${rowErr.message}`);
      setMsg(`${d.doc_id} updated.`);
      await load();
    } catch (e) { setMsg(String((e as Error).message)); }
    finally { setBusy(null); }
  }

  async function call(fn: string, args: Record<string, unknown>, ok: string) {
    setBusy(fn + JSON.stringify(args)); setMsg(null);
    try {
      const { supabase } = await import('../lib/supabase');
      const { error } = await supabase().schema('app').rpc(fn, args);
      if (error) throw new Error(error.message);
      setMsg(ok);
      await load();
    } catch (e) { setMsg(String((e as Error).message)); }
    finally { setBusy(null); }
  }

  async function resolve(d: Decision) {
    if (!answer.trim()) { setMsg('Say what was decided.'); return; }
    await call('canon_resolve_decision', { p_decision: d.id, p_resolution: answer.trim() },
               'Decision recorded.');
    setAnswering(null); setAnswer('');
  }

  async function approve(doc: Doc, openCount: number) {
    let reason: string | null = null;
    if (openCount > 0) {
      /* Not blocked, recorded. A director may have good reason to approve an interim version with
         a question still open; what must not happen is nobody being able to tell afterwards. */
      reason = window.prompt(
        `${openCount} decision(s) on this document are still open.\n\n`
        + 'You can still approve it, but the reason is recorded against the document. '
        + 'Why are you approving it now?');
      if (!reason || !reason.trim()) return;
    }
    const version = window.prompt('Version to approve as:', doc.version === '0.1' ? '1.0' : doc.version);
    if (version === null) return;
    await call('canon_approve',
               { p_document: doc.id, p_version: version, p_override_reason: reason },
               `${doc.doc_id} approved.`);
  }

  if (err) return <p className="block-error" role="alert">{err}</p>;
  if (!rows) return <p className="muted">Loading</p>;

  const missing = rows.filter((d) => !d.file_path).length;
  const openDecs = decs.filter((x) => x.status === 'open');
  const openFor = (docId: string) => openDecs.filter((x) => x.document_id === docId);
  const cats = [...new Set(rows.map((d) => d.category))];

  return (
    <div className="page">
      <div className="page-head">
        <h1 className="page-title">Canon</h1>
        <p className="page-sub">
          The policies and procedures {TENANT.brand.orgName} is bound by. The device quality
          system lives in Quality.
        </p>
      </div>

      {msg && <p className="block-note" role="status">{msg}</p>}

      {/* Said once, at the top, because it is the state of the whole register rather than a
          property of any one document. */}
      {(missing > 0 || openDecs.length > 0 || !mayApprove) && (
        <section className="card canon-summary">
          {missing > 0 && (
            <p><strong>{missing}</strong> of {rows.length} documents have no file attached yet.
              Use Upload on the card.</p>
          )}
          {openDecs.length > 0 && (
            <p><strong>{openDecs.length}</strong> open decision
              {openDecs.length === 1 ? '' : 's'} across{' '}
              {new Set(openDecs.map((d) => d.document_id)).size} documents. Each is on its
              document below.</p>
          )}
          {!mayApprove && (
            /* Said plainly rather than by absent buttons, so somebody who cannot approve knows
               why rather than assuming the feature is missing. */
            <p className="canon-note">You can read and upload. Resolving decisions and approving
              policy needs the owner, admin or senior independent director role.</p>
          )}
        </section>
      )}

      {cats.map((cat) => (
        <section key={cat} className="canon-group">
          <h2 className="canon-cat">{CATEGORY[cat] ?? cat}</h2>
          <div className="canon-grid">
            {rows.filter((d) => d.category === cat).map((d) => (
              <article key={d.id} className={`card canon-doc st-${d.status}`}>
                <div className="canon-head">
                  <span className="canon-id">{d.doc_id}</span>
                  <span className={`canon-status st-${d.status}`}>{STATUS[d.status] ?? d.status}</span>
                </div>
                <h3 className="canon-title">{d.title}</h3>
                {d.summary && <p className="canon-summary-t">{d.summary}</p>}
                {decs.filter((x) => x.document_id === d.id).map((x) => (
                  <div key={x.id} className={`canon-dec ${x.status}`}>
                    <div className="canon-q">
                      {x.status === 'resolved'
                        ? <span className="canon-tick" aria-hidden>✓</span>
                        : <span className="canon-dot" aria-hidden>•</span>}
                      <span>{x.question}</span>
                    </div>
                    {x.status === 'open' && x.detail && <p className="canon-detail">{x.detail}</p>}
                    {x.status === 'resolved' && (
                      <p className="canon-detail">
                        {x.resolution}
                        {x.resolved_at && <> · {x.resolved_at.slice(0, 10)}</>}
                        {mayApprove && (
                          <> · <button className="link-btn"
                                 onClick={() => call('canon_reopen_decision', { p_decision: x.id },
                                                     'Reopened.')}>reopen</button></>
                        )}
                      </p>
                    )}
                    {x.status === 'open' && mayApprove && (
                      answering === x.id ? (
                        <div className="canon-answer">
                          <input className="input grow" autoFocus placeholder="What was decided?"
                                 value={answer} onChange={(e) => setAnswer(e.target.value)}
                                 onKeyDown={(e) => { if (e.key === 'Enter') resolve(x); }} />
                          <button className="btn primary" onClick={() => resolve(x)}>Save</button>
                          <button className="btn" onClick={() => { setAnswering(null); setAnswer(''); }}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button className="link-btn"
                                onClick={() => { setAnswering(x.id); setAnswer(''); }}>
                          Mark as decided
                        </button>
                      )
                    )}
                  </div>
                ))}
                <div className="canon-meta">
                  v{d.version}
                  {d.owner_name && <> · {d.owner_name}</>}
                  {d.approved_on && <> · approved {d.approved_on}
                    {d.approved_by && <> by {d.approved_by}</>}</>}
                  {d.review_due && <> · review {d.review_due}</>}
                  {d.file_size != null && <> · {size(d.file_size)}</>}
                </div>
                {d.approval_note && (
                  <p className="canon-override"><strong>Approved with open decisions.</strong>{' '}
                    {d.approval_note}</p>
                )}
                <CanonSignatures doc={d} />
                <div className="canon-foot">
                  {d.file_path ? (
                    <button className="btn primary" disabled={busy === d.id}
                            onClick={() => download(d)}>
                      {busy === d.id ? 'Preparing' : 'Download'}
                    </button>
                  ) : (
                    <span className="canon-missing">No file attached</span>
                  )}
                  {mayApprove && d.status !== 'approved' && (
                    <button className="btn" onClick={() => approve(d, openFor(d.id).length)}>
                      Approve
                    </button>
                  )}
                  {mayApprove && d.status === 'approved' && (
                    <button className="link-btn"
                            onClick={() => call('canon_set_status',
                                                { p_document: d.id, p_status: 'draft' },
                                                'Returned to draft.')}>
                      return to draft
                    </button>
                  )}
                  <label className="btn canon-upload">
                    {d.file_path ? 'Replace' : 'Upload'}
                    <input type="file" hidden
                           onChange={(e) => {
                             const f = e.target.files?.[0];
                             if (f) upload(d, f);
                             e.target.value = '';
                           }} />
                  </label>
                </div>
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}


/* =====================================================================================
   The signature strip: who has put their name to this document, and the Sign control.
   The typed name IS the ceremony: app.sign_object refuses any name that is not the
   signer's own, and the register is append-only. The content anchor hashes the document's
   identifying fields at signing time so a later reader can see whether it changed.
   ===================================================================================== */
function CanonSignatures({ doc }: { doc: Doc }) {
  const [sigs, setSigs] = useState<{ out_signed_name: string; out_meaning: string;
    out_signed_at: string }[]>([]);
  const load = useCallback(async () => {
    try {
      const { supabase } = await import('../lib/supabase');
      const { data } = await supabase().schema('app')
        .rpc('signatures_for', { p_type: 'canon_document', p_id: doc.id });
      setSigs((data ?? []) as never);
    } catch { /* absent backend: strip stays empty */ }
  }, [doc.id]);
  useEffect(() => { void load(); }, [load]);

  async function sign() {
    const name = window.prompt(
      'Signing adopts this document as policy, in your name, permanently.\n'
      + 'Type your name exactly as Forum knows you:');
    if (!name) return;
    try {
      const { supabase } = await import('../lib/supabase');
      const anchor = `${doc.doc_id}|v${doc.version}|${doc.file_path ?? 'no-file'}|${doc.file_size ?? 0}`;
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(anchor));
      const hash = Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
      const { error } = await supabase().schema('app').rpc('sign_object', {
        p_type: 'canon_document', p_id: doc.id, p_meaning: 'adopted',
        p_typed_name: name, p_label: `${doc.doc_id} v${doc.version}`, p_hash: hash,
      });
      if (error) window.alert(error.message);
      await load();
    } catch (e) { window.alert(String((e as Error).message)); }
  }

  return (
    <div className="canon-sigs">
      {sigs.map((s, i) => (
        <span key={i} className="canon-sig" title={s.out_signed_at}>
          ✍ {s.out_signed_name} · {s.out_meaning} · {String(s.out_signed_at).slice(0, 10)}
        </span>
      ))}
      {doc.status === 'approved' && (
        <button className="link-btn" onClick={sign}>
          {sigs.length ? 'add signature' : 'Sign the adopted policy'}
        </button>
      )}
    </div>
  );
}
